import { DamageClaimResponse, Task } from '@parkingbase/ecommerce-api-dev';

import { create } from 'zustand';
import { DamageClaimService } from '@/services/damage-claim.service';
import { logger } from '@/lib/logger';

const damageClaimService = new DamageClaimService();

interface DamageClaimState {
  claimDetails: DamageClaimResponse | null;
  isLoading: boolean;
  isSubmitting: boolean;
  isSubmitted: boolean;
  reportNumber: string | null;
  fetchDetails: (requestId: string, company?: string) => Promise<void>;
  submitClaim: (
    companyId: string,
    requestId: string,
    damageClaimRequest: string,
    files: File[]
  ) => Promise<Task>;
  reset: () => void;
}

export const useDamageClaimStore = create<DamageClaimState>((set) => ({
  claimDetails: null,
  isLoading: false,
  isSubmitting: false,
  isSubmitted: false,
  reportNumber: null,

  fetchDetails: async (requestId: string, company?: string) => {
    set({ isLoading: true });
    try {
      const claimDetails = await damageClaimService.getDamageClaimDetails(requestId, company);
      set({ claimDetails });
    } catch (error) {
      logger.error('Failed to fetch damage claim details', error, {
        feature: 'damage-claim-store',
        route: 'fetchDetails',
      });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  submitClaim: async (
    companyId: string,
    requestId: string,
    damageClaimRequest: string,
    files: File[]
  ) => {
    set({ isSubmitting: true });
    try {
      const task = await damageClaimService.submitDamageClaim(
        companyId,
        requestId,
        damageClaimRequest,
        files
      );
      set({
        isSubmitted: true,
        reportNumber: task?.documentId ?? null,
      });
      return task;
    } catch (error) {
      logger.error('Failed to submit damage claim', error, {
        feature: 'damage-claim-store',
        route: 'submitClaim',
      });
      throw error;
    } finally {
      set({ isSubmitting: false });
    }
  },

  reset: () => {
    set({
      claimDetails: null,
      isLoading: false,
      isSubmitting: false,
      isSubmitted: false,
      reportNumber: null,
    });
  },
}));
